/*
  qv-qty-limits.js

  Basket-level enforcement of case-pack order quantities, replacing the
  Minmaxify app. Rules are variant metafields (custom.qty_min /
  custom.qty_multiple / custom.qty_max) which snippets/quantity-selector.liquid
  already renders onto every cart line's quantity input as data-qv-qty-*, so
  this file needs no API call - it reads the rules straight off the DOM.

  What it does:
    - validates every cart line on load and whenever a quantity changes
    - blocks the checkout buttons (cart page, sticky bar and drawer all use
      name="checkout") and hides the Shop Pay / PayPal block, which cannot be
      intercepted because it renders in an iframe
    - explains what is wrong, using the same wording the app used

  Deliberately does NOT auto-correct quantities. Silently changing what someone
  put in their basket is worse than telling them; the app asked too, and a
  shopper who wanted 11 should decide between 6 and 12 themselves.

  Limitation, stated plainly: this is client-side, exactly as Minmaxify was.
  Someone constructing a checkout URL by hand bypasses it. Hard enforcement
  needs a Shopify cart-validation Function, which means a custom app.
*/
(function () {
  var MSG = {
    intro: 'You cannot proceed to checkout with this basket selection:',
    min: '{name}: Must have at least {n} of this item.',
    max: '{name}: Must have at most {n} of this item.',
    mult: '{name}: Quantity must be a multiple of {n}.'
  };
  var CHECKOUT = '[name="checkout"]';
  var DYNAMIC = '.additional-checkout-buttons, .dynamic-checkout__content, shopify-payment-button, .shopify-payment-button';

  function fmt(t, name, n) {
    return t.replace('{name}', name).replace('{n}', n);
  }

  function intAttr(el, a) {
    var v = parseInt(el.getAttribute(a), 10);
    return isNaN(v) || v < 1 ? 0 : v;
  }

  function lineName(input) {
    var row = input.closest('[data-cartitem-key], [data-cartitem], .cart-item, .cart__row, [data-cart-item]');
    if (!row) return 'This item';
    // Each line has TWO links to the product: the thumbnail first, which has no
    // text, then the title. Taking querySelector's first match returned an empty
    // string and every message read "This item". Take the first link that
    // actually has text.
    var links = row.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var t = (links[i].textContent || '').trim();
      if (t) return t;
    }
    var heading = row.querySelector('.cart-item__title, .cart-item--title, [data-cart-item-title]');
    var ht = heading && heading.textContent ? heading.textContent.trim() : '';
    return ht || 'This item';
  }

  // Cart lines only. The product page input is named "quantity" and is handled
  // by its own min/step plus the guard at the bottom of this file.
  function cartInputs() {
    return Array.prototype.filter.call(
      document.querySelectorAll('input[data-quantity-input]'),
      function (i) { return (i.name || '').indexOf('updates') === 0; }
    );
  }

  function problems() {
    var out = [];
    cartInputs().forEach(function (inp) {
      var qty = parseInt(inp.value, 10);
      if (!qty || qty < 1) return;
      var mn = intAttr(inp, 'data-qv-qty-min');
      var ml = intAttr(inp, 'data-qv-qty-multiple');
      var mx = intAttr(inp, 'data-qv-qty-max');
      if (!mn && !ml && !mx) return;
      var name = lineName(inp);
      // One message per line, most useful first: a cap is unambiguous, then
      // being under the minimum, then the pack multiple. Listing all three for
      // one line just makes the dialog harder to act on.
      if (mx && qty > mx) out.push(fmt(MSG.max, name, mx));
      else if (mn && qty < mn) out.push(fmt(MSG.min, name, mn));
      else if (ml && qty % ml !== 0) out.push(fmt(MSG.mult, name, ml));
    });
    return out;
  }

  function apply() {
    var bad = problems();
    var blocked = bad.length > 0;
    document.documentElement.classList.toggle('qv-qty-blocked', blocked);
    Array.prototype.forEach.call(document.querySelectorAll(CHECKOUT), function (b) {
      // aria-disabled rather than disabled: a disabled button in the drawer's
      // form stops the form serialising, and the theme submits that form to
      // update quantities.
      b.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      b.classList.toggle('qv-qty-blocked-btn', blocked);
    });
    return bad;
  }

  var dlg = null;
  function show(messages) {
    close();
    dlg = document.createElement('div');
    dlg.className = 'qv-qty-dialog';
    dlg.setAttribute('role', 'alertdialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-label', MSG.intro);
    var list = messages.map(function (m) {
      var li = document.createElement('li');
      li.textContent = m;
      return li.outerHTML;
    }).join('');
    dlg.innerHTML =
      '<div class="qv-qty-dialog__bg" data-qv-close></div>' +
      '<div class="qv-qty-dialog__panel">' +
        '<p class="qv-qty-dialog__intro"></p>' +
        '<ul class="qv-qty-dialog__list">' + list + '</ul>' +
        '<button type="button" class="qv-qty-dialog__ok" data-qv-close>OK</button>' +
      '</div>';
    dlg.querySelector('.qv-qty-dialog__intro').textContent = MSG.intro;
    document.body.appendChild(dlg);
    var ok = dlg.querySelector('.qv-qty-dialog__ok');
    if (ok) ok.focus();
  }

  function close() {
    if (dlg && dlg.parentNode) dlg.parentNode.removeChild(dlg);
    dlg = null;
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-qv-close]')) { close(); return; }
    var btn = e.target.closest && e.target.closest(CHECKOUT);
    if (!btn) return;
    var bad = apply();
    if (!bad.length) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    show(bad);
  }, true);

  document.addEventListener('keyup', function (e) { if (e.key === 'Escape') close(); });

  ['input', 'change'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (e.target && e.target.matches && e.target.matches('input[data-quantity-input]')) apply();
    }, true);
  });

  // The drawer and the cart page both re-render their line list after an
  // update, which replaces the inputs this reads from.
  function watch() {
    apply();
    if (!('MutationObserver' in window)) return;

    // Scoped to the cart containers, NOT document.body.
    //
    // This previously observed the whole body subtree, so every DOM change made
    // by any script on the page - and there are a lot of them, between the
    // review widget, Klaviyo and the rest - re-ran apply(), which walks the
    // document twice with querySelectorAll. On a collection page with 35
    // product cards and ~5,300 nodes that is real main-thread work, repeated,
    // for no benefit: the only things apply() cares about are cart lines, which
    // only ever appear inside the cart page or the drawer.
    //
    // If neither container is on the page there is nothing to watch at all.
    var targets = document.querySelectorAll(
      '[data-cart-item-list], .cartitems, [data-atc-banner], .atc-banner'
    );
    if (!targets.length) return;

    var debounce;
    var observer = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(apply, 150);
    });
    Array.prototype.forEach.call(targets, function (t) {
      observer.observe(t, { childList: true, subtree: true });
    });
  }

  window.addEventListener('cartcount:update', apply);

  // NOTE: there was a submit-phase guard on form[action*="/cart/add"] here.
  // Removed 2026-08-18: adding a case-pack product stopped opening the cart
  // drawer and navigated to /cart instead, which is what a native form submit
  // does when the theme's own jQuery submit handler never gets to run. This
  // listener was the only thing of ours sitting in that path, so it goes.
  //
  // Nothing is lost that matters: the quantity input already carries min, step
  // and max from quantity-selector.liquid, so the browser will not accept an
  // invalid quantity on the product page, and the basket-level check below is
  // the real backstop either way.

  // Small public surface. Exposed so the behaviour can be checked from the
  // console without clicking through a basket, and so any other script (the
  // cart drawer's own re-render, for one) can re-validate after it changes the
  // DOM instead of waiting for the MutationObserver.
  window.qvQtyLimits = {
    validate: apply,
    problems: problems,
    show: show,
    close: close
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
